/**
 * ScreenCaptureService — Captures route, page title, and sanitized DOM snapshots.
 *
 * Used during voice recording to associate feedback comments with the
 * screen(s) being viewed. The feedback widget's own DOM is excluded.
 */

import type { ScreenCapture } from "../types/feedback";

export function captureScreen(): ScreenCapture {
  const mainContent = document.querySelector("#root");

  let htmlSnapshot = "";
  if (mainContent) {
    const clone = mainContent.cloneNode(true) as HTMLElement;

    // Remove the feedback widget from the snapshot
    clone.querySelector("#feedback-widget")?.remove();

    // Strip script, style, and link[rel=stylesheet] tags
    clone
      .querySelectorAll('script, style, link[rel="stylesheet"]')
      .forEach((el) => el.remove());

    // Strip on* event handler attributes
    sanitizeAttributes(clone);

    htmlSnapshot = clone.outerHTML;
  }

  return {
    route: window.location.pathname,
    pageTitle: document.title,
    htmlSnapshot,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    capturedAt: new Date().toISOString(),
  };
}

/**
 * Recursively removes on* attributes and data-* attributes from all elements.
 */
function sanitizeAttributes(element: HTMLElement): void {
  const attributesToRemove: string[] = [];

  for (const attr of Array.from(element.attributes)) {
    if (attr.name.startsWith("on") || attr.name.startsWith("data-")) {
      attributesToRemove.push(attr.name);
    }
  }

  for (const name of attributesToRemove) {
    element.removeAttribute(name);
  }

  for (const child of Array.from(element.children)) {
    if (child instanceof HTMLElement) {
      sanitizeAttributes(child);
    }
  }
}
